import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText, Upload, Eye, Clock, CheckCircle, XCircle, Shield,
    Loader2, RefreshCw, History, ShieldCheck, ThumbsUp, ThumbsDown,
    Maximize2, X, AlertCircle, Image, File, Building2, Trash2, Users
} from 'lucide-react';
import { kycService } from '@services/kycService';
import { companyService } from '@services/companyService';
import { clientService } from '@services/clientService';
import { useAuth } from '@context/AuthContext';
import CustomSelect from '../shared/CustomSelect';

const STATUS_CONFIG = {
    APPROVED: { color: 'bg-green-100 text-green-800', dot: 'bg-green-500', icon: CheckCircle, label: 'Approved' },
    VERIFIED: { color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500', icon: Shield, label: 'Verified' },
    REJECTED: { color: 'bg-red-100 text-red-800', dot: 'bg-red-500', icon: XCircle, label: 'Rejected' },
    PENDING:  { color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500', icon: Clock, label: 'Pending' },
    EXPIRED:  { color: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400', icon: Clock, label: 'Expired' },
};

const getStatus = (s) => STATUS_CONFIG[s] || { color: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400', icon: Clock, label: s || 'Unknown' };

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

const isImageUrl = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.png') ||
           lower.includes('image/jpeg') || lower.includes('image/png');
};

const isPdfUrl = (url) => {
    if (!url) return false;
    return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('application/pdf');
};

const BO_TYPES = ['BO_DECLARATION', 'BO_REGISTER', 'OWNERSHIP_CHAIN', 'SOURCE_OF_WEALTH', 'SOURCE_OF_FUNDS', 'CR12_FORM'];

// Organize checklist items into groups and standalone items, preserving order
const organizeItems = (items) => {
    if (!items || items.length === 0) return [];
    const result = [];
    const seenGroups = new Set();

    for (const item of items) {
        if (item.groupName) {
            if (!seenGroups.has(item.groupName)) {
                seenGroups.add(item.groupName);
                const groupItems = items.filter(i => i.groupName === item.groupName);
                result.push({
                    type: 'group',
                    groupName: item.groupName,
                    groupSatisfied: groupItems[0]?.groupSatisfied || false,
                    items: groupItems,
                });
            }
        } else {
            result.push({ type: 'standalone', item });
        }
    }
    return result;
};

const formatGroupName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

const ClientKycDocuments = ({ clientId, client }) => {
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [checklist, setChecklist] = useState(null);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingChecklist, setLoadingChecklist] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [rejectingDoc, setRejectingDoc] = useState(null);
    const [rejectReasonCode, setRejectReasonCode] = useState('');
    const [rejectComment, setRejectComment] = useState('');
    const [rejectionReasons, setRejectionReasons] = useState([]);
    const [actionLoading, setActionLoading] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Role-based permissions
    const allRoles = [
        user?.role,
        ...(user?.roles || [])
    ].filter(Boolean).map(r => r.toUpperCase());

    const hasRole = (...roles) => roles.some(r => allRoles.includes(r));
    const noRoleInfo = allRoles.length === 0;
    const canVerify = noRoleInfo || hasRole('SUPER_ADMIN', 'ADMIN', 'MANAGER');
    const canApprove = noRoleInfo || hasRole('SUPER_ADMIN', 'ADMIN');
    const canReject = noRoleInfo || hasRole('SUPER_ADMIN', 'ADMIN', 'MANAGER');
    const canDelete = noRoleInfo || hasPermission('KYC:DELETE') || hasRole('SUPER_ADMIN', 'ADMIN', 'MANAGER', 'DATA_OFFICER');

    // Fetch companies the client belongs to, with fallback to all companies
    useEffect(() => {
        fetchCompanies();
    }, [clientId]);

    // Rejection reason catalog is served by the backend so codes stay in sync
    useEffect(() => {
        kycService.clientKyc.getRejectionReasons()
            .then((response) => {
                const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
                setRejectionReasons(data);
            })
            .catch((err) => console.error('Error fetching rejection reasons:', err));
    }, []);

    // Fetch checklist when company is selected
    useEffect(() => {
        if (selectedCompanyId && clientId) {
            fetchChecklist();
        } else {
            setChecklist(null);
            setSelectedDoc(null);
        }
    }, [selectedCompanyId, clientId]);

    const fetchCompanies = async () => {
        try {
            setLoadingCompanies(true);
            // Try to get companies the client has products with first
            let data = [];
            try {
                const clientCompaniesResp = await clientService.getCompanies(clientId);
                if (clientCompaniesResp.success && clientCompaniesResp.data && clientCompaniesResp.data.length > 0) {
                    data = clientCompaniesResp.data;
                }
            } catch (e) {
                // Fallback silently
            }

            // Fallback to all companies if no client-specific companies found
            if (data.length === 0) {
                const allCompaniesResp = await companyService.getAll();
                data = (allCompaniesResp.success && allCompaniesResp.data) ? allCompaniesResp.data : [];
            }

            setCompanies(data.filter(c => c.active !== false));
            if (data.length > 0) {
                setSelectedCompanyId(data[0].id);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const fetchChecklist = async () => {
        try {
            setLoadingChecklist(true);
            setError('');
            const response = await kycService.clientKyc.getChecklist(clientId, selectedCompanyId);
            if (response.success && response.data) {
                setChecklist(response.data);
                // Auto-select first submitted doc for preview
                const allItems = [...(response.data.standardKyc || []), ...(response.data.beneficialOwnership || [])];
                const firstSubmitted = allItems.find(item => item.submissionId);
                if (firstSubmitted && firstSubmitted.submissionId) {
                    setSelectedDoc(firstSubmitted);
                } else {
                    setSelectedDoc(null);
                }
            }
        } catch (err) {
            console.error('Error fetching checklist:', err);
            setError('Failed to load KYC checklist');
            setChecklist(null);
        } finally {
            setLoadingChecklist(false);
        }
    };

    const showSuccess = (msg) => {
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(''), 3000);
    };

    const handleVerify = async (docId) => {
        try {
            setActionLoading(docId);
            const response = await kycService.clientKyc.verify(docId);
            if (response.success) {
                showSuccess('Document verified successfully');
                fetchChecklist();
            } else {
                setError(response.message || 'Failed to verify document');
            }
        } catch (err) {
            setError(err.message || 'Failed to verify document');
        } finally {
            setActionLoading(null);
        }
    };

    const handleApprove = async (docId) => {
        try {
            setActionLoading(docId);
            const response = await kycService.clientKyc.approve(docId);
            if (response.success) {
                showSuccess('Document approved successfully');
                fetchChecklist();
            } else {
                setError(response.message || 'Failed to approve document');
            }
        } catch (err) {
            setError(err.message || 'Failed to approve document');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (docId) => {
        if (!canSubmitRejection) return;
        try {
            setActionLoading(docId);
            const response = await kycService.clientKyc.reject(docId, rejectReasonCode, rejectComment.trim());
            if (response.success) {
                showSuccess('Document rejected');
                closeRejectForm();
                fetchChecklist();
            } else {
                setError(response.message || 'Failed to reject document');
            }
        } catch (err) {
            setError(err.message || 'Failed to reject document');
        } finally {
            setActionLoading(null);
        }
    };

    const closeRejectForm = () => {
        setRejectingDoc(null);
        setRejectReasonCode('');
        setRejectComment('');
    };

    // OTHER has no self-explanatory label, so it must carry a comment
    const canSubmitRejection = !!rejectReasonCode
        && (rejectReasonCode !== 'OTHER' || rejectComment.trim().length > 0);

    const handleDelete = async (docId) => {
        try {
            setActionLoading(docId);
            const response = await kycService.clientKyc.delete(docId);
            if (response.success) {
                showSuccess('Document deleted successfully');
                setDeleteConfirm(null);
                if (selectedDoc?.submissionId === docId) {
                    setSelectedDoc(null);
                }
                fetchChecklist();
            } else {
                setError(response.message || 'Failed to delete document');
            }
        } catch (err) {
            setError(err.message || 'Failed to delete document');
        } finally {
            setActionLoading(null);
        }
    };

    const companyOptions = companies.map(c => ({ value: c.id, label: c.name }));

    if (loadingCompanies) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    KYC Documents
                </h4>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchChecklist}
                        disabled={!selectedCompanyId}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
                        title="Refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => navigate(`/clients/${clientId}/kyc/submit`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    >
                        <Upload className="w-4 h-4" />
                        Add Document
                    </button>
                </div>
            </div>

            {/* Company Selector */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    Company
                </label>
                {companies.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No companies found for this client</p>
                ) : companies.length <= 5 ? (
                    <div className="flex gap-2 flex-wrap">
                        {companies.map(company => (
                            <button
                                key={company.id}
                                onClick={() => setSelectedCompanyId(company.id)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg border transition ${
                                    selectedCompanyId === company.id
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {company.name}
                            </button>
                        ))}
                    </div>
                ) : (
                    <CustomSelect
                        value={selectedCompanyId}
                        onChange={setSelectedCompanyId}
                        options={companyOptions}
                        placeholder="Select a company"
                        searchable
                    />
                )}
            </div>

            {/* Toasts */}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> {successMessage}
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> {error}
                    <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                </div>
            )}

            {/* Content */}
            {!selectedCompanyId ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Select a company to view KYC checklist</p>
                </div>
            ) : loadingChecklist ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
            ) : checklist ? (
                <>
                    {/* Progress Summary */}
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">Completion Progress</h4>
                                <p className="text-xs text-gray-500">{checklist.companyName}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xl font-bold text-gray-900">
                                    {checklist.mandatoryApproved}/{checklist.mandatoryTotal}
                                </p>
                                <p className="text-xs text-gray-500">mandatory approved</p>
                            </div>
                        </div>
                        {checklist.mandatoryTotal > 0 && (
                            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                                <div
                                    className={`h-2 rounded-full transition-all ${checklist.complete ? 'bg-green-600' : 'bg-blue-600'}`}
                                    style={{ width: `${Math.round((checklist.mandatoryApproved / checklist.mandatoryTotal) * 100)}%` }}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> {checklist.mandatoryApproved} approved</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> {checklist.mandatoryPending} pending</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> {checklist.mandatoryMissing} missing</span>
                            {checklist.optionalTotal > 0 && (
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-300" /> {checklist.optionalSubmitted}/{checklist.optionalTotal} optional</span>
                            )}
                        </div>
                    </div>

                    {/* Checklist + Detail Split View */}
                    <div className="flex gap-4 border border-gray-200 rounded-lg bg-white overflow-hidden" style={{ minHeight: '480px' }}>
                        {/* Left: Checklist */}
                        <div className="w-80 flex-shrink-0 border-r border-gray-200 overflow-y-auto">
                            {/* Standard KYC Section */}
                            {checklist.standardKyc && checklist.standardKyc.length > 0 && (
                                <div>
                                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-blue-600" />
                                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Standard KYC</span>
                                        <span className="text-xs text-gray-400">({checklist.standardKyc.length})</span>
                                    </div>
                                    <ChecklistSectionItems
                                        items={checklist.standardKyc}
                                        selectedDoc={selectedDoc}
                                        onSelect={setSelectedDoc}
                                    />
                                </div>
                            )}

                            {/* Beneficial Ownership Section */}
                            {checklist.beneficialOwnership && checklist.beneficialOwnership.length > 0 && (
                                <div>
                                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                                        <Users className="w-4 h-4 text-amber-600" />
                                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Beneficial Ownership</span>
                                        <span className="text-xs text-gray-400">({checklist.beneficialOwnership.length})</span>
                                    </div>
                                    <ChecklistSectionItems
                                        items={checklist.beneficialOwnership}
                                        selectedDoc={selectedDoc}
                                        onSelect={setSelectedDoc}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Right: Preview + Details + Actions */}
                        {selectedDoc ? (
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {selectedDoc.submissionId ? (
                                    <>
                                        {/* Document preview */}
                                        <div className="flex-1 bg-gray-100 relative flex items-center justify-center overflow-hidden min-h-0">
                                            <DocumentPreview doc={selectedDoc} />
                                        </div>

                                        {/* Details & actions bar */}
                                        <div className="border-t border-gray-200 p-4 bg-white">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h3 className="text-base font-semibold text-gray-900">
                                                            {selectedDoc.requirementName}
                                                        </h3>
                                                        <StatusBadge status={selectedDoc.status} />
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 flex-wrap">
                                                        {selectedDoc.documentNumber && (
                                                            <span>Doc #: <span className="text-gray-700 font-medium">{selectedDoc.documentNumber}</span></span>
                                                        )}
                                                        <span>Version: <span className="text-gray-700 font-medium">v{selectedDoc.version || 1}</span></span>
                                                        <span>Submitted: <span className="text-gray-700">{formatDate(selectedDoc.submittedAt || selectedDoc.updatedAt)}</span></span>
                                                    </div>
                                                    {selectedDoc.notes && (
                                                        <p className="text-xs text-gray-500 mt-1 italic">"{selectedDoc.notes}"</p>
                                                    )}
                                                    {selectedDoc.status === 'REJECTED' && selectedDoc.rejectionReason && (
                                                        <p className="text-xs text-red-600 mt-1 bg-red-50 px-2 py-1 rounded inline-block">
                                                            Rejected: {selectedDoc.rejectionReason}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Action buttons */}
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <button
                                                        onClick={() => navigate(`/clients/${clientId}/kyc/${selectedDoc.submissionId}/history`)}
                                                        className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5"
                                                    >
                                                        <History className="w-3.5 h-3.5" />
                                                        History
                                                    </button>

                                                    {canVerify && selectedDoc.status === 'PENDING' && (
                                                        <button
                                                            onClick={() => handleVerify(selectedDoc.submissionId)}
                                                            disabled={actionLoading === selectedDoc.submissionId}
                                                            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1.5"
                                                        >
                                                            {actionLoading === selectedDoc.submissionId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                                                            Verify
                                                        </button>
                                                    )}

                                                    {canApprove && selectedDoc.status === 'VERIFIED' && (
                                                        <button
                                                            onClick={() => handleApprove(selectedDoc.submissionId)}
                                                            disabled={actionLoading === selectedDoc.submissionId}
                                                            className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-1.5"
                                                        >
                                                            {actionLoading === selectedDoc.submissionId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                                                            Approve
                                                        </button>
                                                    )}

                                                    {canReject && (selectedDoc.status === 'PENDING' || selectedDoc.status === 'VERIFIED') && (
                                                        <button
                                                            onClick={() => (rejectingDoc === selectedDoc.submissionId ? closeRejectForm() : setRejectingDoc(selectedDoc.submissionId))}
                                                            className={`px-3 py-1.5 text-xs rounded-lg transition flex items-center gap-1.5 ${
                                                                rejectingDoc === selectedDoc.submissionId
                                                                    ? 'bg-red-600 text-white'
                                                                    : 'border border-red-300 text-red-600 hover:bg-red-50'
                                                            }`}
                                                        >
                                                            <ThumbsDown className="w-3.5 h-3.5" />
                                                            Reject
                                                        </button>
                                                    )}

                                                    {canDelete && (
                                                        <button
                                                            onClick={() => setDeleteConfirm(selectedDoc.submissionId)}
                                                            className="px-3 py-1.5 text-xs border border-gray-300 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition flex items-center gap-1.5"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Rejection reason inline */}
                                            {rejectingDoc === selectedDoc.submissionId && (
                                                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                                                    <div className="flex items-start gap-2">
                                                        <select
                                                            value={rejectReasonCode}
                                                            onChange={(e) => setRejectReasonCode(e.target.value)}
                                                            className="w-64 px-3 py-1.5 text-sm border border-red-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                                            autoFocus
                                                        >
                                                            <option value="">Select a rejection reason...</option>
                                                            {rejectionReasons.map(reason => (
                                                                <option key={reason.code} value={reason.code}>{reason.label}</option>
                                                            ))}
                                                        </select>
                                                        <textarea
                                                            value={rejectComment}
                                                            onChange={(e) => setRejectComment(e.target.value)}
                                                            rows="2"
                                                            placeholder={rejectReasonCode === 'OTHER'
                                                                ? 'Comment required for "Other"...'
                                                                : 'Add a comment (optional)...'}
                                                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Escape') closeRejectForm();
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleReject(selectedDoc.submissionId)}
                                                            disabled={!canSubmitRejection || actionLoading === selectedDoc.submissionId}
                                                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                                                        >
                                                            {actionLoading === selectedDoc.submissionId ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                                            Confirm Rejection
                                                        </button>
                                                        <button
                                                            onClick={closeRejectForm}
                                                            className="px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Delete confirmation inline */}
                                            {deleteConfirm === selectedDoc.submissionId && (
                                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                                                    <p className="text-sm text-red-600 flex-1">Are you sure? This will permanently delete this document.</p>
                                                    <button
                                                        onClick={() => handleDelete(selectedDoc.submissionId)}
                                                        disabled={actionLoading === selectedDoc.submissionId}
                                                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                                                    >
                                                        {actionLoading === selectedDoc.submissionId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                                        Delete
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(null)}
                                                        className="px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    /* Not yet submitted — show prompt to upload */
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center p-8">
                                            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                                                selectedDoc.mandatory ? 'bg-red-50' : 'bg-gray-50'
                                            }`}>
                                                <FileText className={`w-8 h-8 ${selectedDoc.mandatory ? 'text-red-400' : 'text-gray-400'}`} />
                                            </div>
                                            <h3 className="text-lg font-medium text-gray-900 mb-1">{selectedDoc.requirementName}</h3>
                                            <p className="text-sm text-gray-500 mb-1">
                                                {selectedDoc.description || 'No description'}
                                            </p>
                                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full mb-4 ${
                                                selectedDoc.mandatory ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {selectedDoc.mandatory ? 'Required' : 'Optional'}
                                            </span>
                                            <div>
                                                <button
                                                    onClick={() => navigate(`/clients/${clientId}/kyc/submit`)}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 mx-auto"
                                                >
                                                    <Upload className="w-4 h-4" />
                                                    Upload Document
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-gray-400">
                                <div className="text-center">
                                    <Eye className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Select a requirement to view details</p>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : null}
        </div>
    );
};

/* ---------- Sub-components ---------- */

const ChecklistSectionItems = ({ items, selectedDoc, onSelect }) => {
    const organized = organizeItems(items);

    return (
        <div>
            {organized.map((entry, idx) => {
                if (entry.type === 'group') {
                    return (
                        <div key={entry.groupName}>
                            {/* Group header */}
                            <div className="px-4 py-2 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs font-semibold text-gray-700">
                                        {formatGroupName(entry.groupName)}
                                    </span>
                                    <span className="text-[10px] text-gray-400 italic">— submit any one</span>
                                </div>
                                {entry.groupSatisfied ? (
                                    <span className="flex items-center gap-1 text-[10px] font-medium text-green-600">
                                        <CheckCircle className="w-3 h-3" />
                                        Met
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-[10px] font-medium text-red-500">
                                        <AlertCircle className="w-3 h-3" />
                                        Missing
                                    </span>
                                )}
                            </div>
                            {/* Group items indented */}
                            {entry.items.map(item => (
                                <ChecklistRow
                                    key={item.requirementId}
                                    item={item}
                                    isSelected={selectedDoc?.requirementId === item.requirementId}
                                    onClick={() => onSelect(item)}
                                    indented
                                />
                            ))}
                        </div>
                    );
                }
                return (
                    <ChecklistRow
                        key={entry.item.requirementId}
                        item={entry.item}
                        isSelected={selectedDoc?.requirementId === entry.item.requirementId}
                        onClick={() => onSelect(entry.item)}
                    />
                );
            })}
        </div>
    );
};

const ChecklistRow = ({ item, isSelected, onClick, indented }) => {
    const hasSubmission = item.submissionId != null;
    const st = hasSubmission ? getStatus(item.status) : null;

    return (
        <button
            onClick={onClick}
            className={`w-full text-left py-3 border-b border-gray-100 transition hover:bg-gray-50 ${
                indented ? 'pl-7 pr-4' : 'px-4'
            } ${
                isSelected ? 'bg-blue-50 border-l-[3px] border-l-blue-600' : 'border-l-[3px] border-l-transparent'
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                    {/* Status icon */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        hasSubmission && item.status === 'APPROVED' ? 'bg-green-100' :
                        hasSubmission && item.status === 'VERIFIED' ? 'bg-blue-100' :
                        hasSubmission && item.status === 'REJECTED' ? 'bg-red-100' :
                        hasSubmission ? 'bg-yellow-100' :
                        item.mandatory ? 'bg-red-50' : 'bg-gray-50'
                    }`}>
                        {hasSubmission && item.status === 'APPROVED' ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> :
                         hasSubmission && item.status === 'VERIFIED' ? <Shield className="w-3.5 h-3.5 text-blue-600" /> :
                         hasSubmission && item.status === 'REJECTED' ? <XCircle className="w-3.5 h-3.5 text-red-600" /> :
                         hasSubmission ? <Clock className="w-3.5 h-3.5 text-yellow-600" /> :
                         item.mandatory ? <AlertCircle className="w-3.5 h-3.5 text-red-300" /> :
                         <FileText className="w-3.5 h-3.5 text-gray-300" />}
                    </div>
                    <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                            {item.requirementName}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            {item.mandatory ? (
                                <span className="text-[10px] font-medium text-red-600">Required</span>
                            ) : (
                                <span className="text-[10px] font-medium text-gray-400">Optional</span>
                            )}
                            {hasSubmission && (
                                <span className="text-[10px] text-gray-400">
                                    &middot; v{item.version}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {hasSubmission && st && (
                    <span className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded ${st.color}`}>
                        {st.label}
                    </span>
                )}
                {!hasSubmission && (
                    <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500">
                        Missing
                    </span>
                )}
            </div>
        </button>
    );
};

const StatusBadge = ({ status }) => {
    const st = getStatus(status);
    const Icon = st.icon;
    return (
        <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 w-fit ${st.color}`}>
            <Icon className="w-3 h-3" />
            {st.label}
        </span>
    );
};

const DocumentPreview = ({ doc }) => {
    const [fullscreen, setFullscreen] = useState(false);
    const url = doc.documentUrl;

    if (!url) {
        return (
            <div className="text-center text-gray-400 p-8">
                <File className="w-12 h-12 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No file uploaded</p>
            </div>
        );
    }

    const isImage = isImageUrl(url);
    const isPdf = isPdfUrl(url);

    if (fullscreen) {
        return (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setFullscreen(false)}>
                <button
                    onClick={() => setFullscreen(false)}
                    className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition z-10"
                >
                    <X className="w-6 h-6" />
                </button>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-4 right-16 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition z-10"
                    onClick={(e) => e.stopPropagation()}
                >
                    Open in new tab
                </a>
                {isImage ? (
                    <img src={url} alt={doc.requirementName} className="max-h-[90vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
                ) : isPdf ? (
                    <iframe src={url} title={doc.requirementName} className="w-[90vw] h-[90vh] bg-white rounded" onClick={(e) => e.stopPropagation()} />
                ) : null}
            </div>
        );
    }

    return (
        <>
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                <button onClick={() => setFullscreen(true)} className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-700 transition" title="Enlarge">
                    <Maximize2 className="w-4 h-4" />
                </button>
                <a href={url} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-700 transition" title="Open in new tab">
                    <Eye className="w-4 h-4" />
                </a>
            </div>
            {isImage ? (
                <img src={url} alt={doc.requirementName} className="max-h-full max-w-full object-contain cursor-pointer" onClick={() => setFullscreen(true)} />
            ) : isPdf ? (
                <iframe src={url} title={doc.requirementName} className="w-full h-full border-0" />
            ) : (
                <div className="text-center text-gray-400 p-8">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-40" />
                    <p className="text-sm mb-2">Cannot preview this file type</p>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                        Download / Open file
                    </a>
                </div>
            )}
        </>
    );
};

export default ClientKycDocuments;
